import { useObserver, useSubtree } from "subtree.js/react";
import { CustomerTabs } from "../../components/CustomerTabs";
import { LoadFailed, Loading } from "../../components/LoadState";
import { CustomerCartState } from "../../sections/customer-cart/customer-cart-controller";
import { CartPayment, CustomerCartTile } from "../../sections/customer-cart/customer-cart-tile";
import { CustomerCartsActions, CustomerCartsState } from "./customer-carts-model";

export function CustomerCartsPage() {
  const state = useSubtree(CustomerCartsState);
  const actions = useSubtree(CustomerCartsActions);
  const cartState = useSubtree(CustomerCartState);
  const observer = useObserver();
  const shopId = observer.watch(state.shopId);
  const status = observer.watch(state.status);
  const carts = observer.watch(state.carts);
  const actionError = observer.watch(cartState.actionError);
  const openCarts = observer.watch(state.openCarts);

  if (!shopId) {
    return (
      <div className="mx-auto max-w-4xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
        <h1 className="mb-4 text-xl font-bold text-white">Customer Carts</h1>
        <p className="text-sm text-muted">We're still loading your shop. Refresh if this doesn't clear.</p>
      </div>
    );
  }

  const isLoading = status.kind === "idle" || status.kind === "loading";
  const isError = status.kind === "error";
  // A read over carts already shown keeps them, so a change's read-back leaves the focus and the scroll where they were.
  const shown = carts.length > 0;

  return (
    <div className="mx-auto max-w-4xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
      {/* In the panel the Scans and Shop QR tabs sit in, so the tabs do not move from one to the next. */}
      <div className="panel p-4 sm:p-6">
        <CustomerTabs active="carts" openCarts={openCarts} customer={state.customerId} />

        {actionError && (
          <p role="alert" className="note note-danger mb-4 text-danger">
            {actionError}
          </p>
        )}

        {isLoading && !shown && <Loading label="Loading customer carts…" />}

        {status.kind === "error" && (
          <LoadFailed title="Failed to load customer carts." message={status.message} onRetry={actions.reload} />
        )}

        {!isLoading && !isError && !shown && (
          <div className="py-12 text-center">
            <p className="mb-1 text-base font-medium text-white">No customer carts yet</p>
            <p className="text-sm text-muted">Carts customers build in your shop show up here.</p>
          </div>
        )}

        {shown && (
          // Two carts a row once the column has room for two tiles of iOS's 320-point minimum.
          <div className="@container">
            <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2">
              {carts.map((cart) => (
                <CustomerCartTile key={cart.id} cart={cart} />
              ))}
            </div>
          </div>
        )}
      </div>

      <CartPayment />
    </div>
  );
}
